"use client";

import { Button } from "@/components/ui/button";
import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

export type TimelineEntry = {
	title: string;
	subtitle: string;
	description: string;
	items?: string[];
	image?: string;
	button?: {
		url: string;
		text: string;
	};
};

export interface TimelineProps {
	title?: string;
	description?: string;
	entries?: TimelineEntry[];
	className?: string;
}

/**
 * Behavior: Only the card that is currently centered in the viewport is "open".
 * As you scroll, the active card expands to reveal its full content. Others stay collapsed.
 */
export default function Timeline({
	entries,
	title,
	description,
	className,
}: TimelineProps) {
	const [activeIndex, setActiveIndex] = useState(0);
	const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
	const sentinelRefs = useRef<(HTMLDivElement | null)[]>([]);

	// Create stable setters for refs inside map
	const setItemRef = (el: HTMLDivElement | null, i: number) => {
		itemRefs.current[i] = el;
	};
	const setSentinelRef = (el: HTMLDivElement | null, i: number) => {
		sentinelRefs.current[i] = el;
	};

	useEffect(() => {
		if (!sentinelRefs.current.length) return;

		// We observe small sentinels placed near the title of each card. Whichever
		// sentinel is closest to the vertical center of the viewport becomes active.
		// Using IntersectionObserver to track visibility + a rAF loop to pick the closest.

		let frame = 0;
		const updateActiveByProximity = () => {
			frame = requestAnimationFrame(updateActiveByProximity);
			// Compute distance of each sentinel to viewport center
			const centerY = window.innerHeight / 3;
			let bestIndex = 0;
			let bestDist = Number.POSITIVE_INFINITY;
			sentinelRefs.current.forEach((node, i) => {
				if (!node) return;
				const rect = node.getBoundingClientRect();
				const mid = rect.top + rect.height / 2;
				const dist = Math.abs(mid - centerY);
				if (dist < bestDist) {
					bestDist = dist;
					bestIndex = i;
				}
			});
			if (bestIndex !== activeIndex) setActiveIndex(bestIndex);
		};

		frame = requestAnimationFrame(updateActiveByProximity);
		return () => cancelAnimationFrame(frame);
	}, [activeIndex]);

	return (
		<section className="px-6">
			<div className="mx-auto max-w-3xl">
				<h1 className="mb-4 text-3xl font-bold tracking-tight md:text-5xl">
					{title}
				</h1>
				<p className="mb-6 text-base text-muted-foreground md:text-lg">
					{description}
				</p>
			</div>

			<div className="mx-auto mt-16 max-w-3xl space-y-16 md:mt-24 md:space-y-24">
				{entries?.map((entry, index) => {
					const isActive = index === activeIndex;

					return (
						<div
							// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
							key={index}
							className="relative flex flex-col gap-4 md:flex-row md:gap-16"
							ref={(el) => setItemRef(el, index)}
							aria-current={isActive ? "true" : "false"}
						>
							{/* Sticky meta column */}
							<div className="top-8 flex h-min w-64 shrink-0 items-center gap-4 md:sticky">
								<div className="flex items-center gap-3">
									<div className="flex flex-col">
										<span className="text-sm font-medium">{entry.title}</span>
										<span className="text-xs text-muted-foreground">
											{entry.subtitle}
										</span>
									</div>
								</div>
							</div>

							{/* Invisible sentinel near the card title to measure proximity to viewport center */}
							<div
								ref={(el) => setSentinelRef(el, index)}
								aria-hidden
								className="absolute -top-24 left-0 h-12 w-12 opacity-0"
							/>

							{/* Content column */}
							<article
								className={`flex flex-col rounded-2xl border p-3 transition-all duration-300 ${
									isActive
										? "border-primary/20 bg-primary/5 shadow-lg"
										: "border-primary/10 bg-primary/5"
								}`}
							>
								{entry.image && (
									<Image
										src={entry.image}
										alt={`${entry.title} visual`}
										className="mb-4 w-full h-48 rounded-lg object-cover object-top"
										loading="lazy"
										width={800}
										height={400}
									/>
								)}
								<div className="space-y-4">
									{/* Header with improved typography */}
									<div className="space-y-2">
										<h2
											className={`text-md font-medium leading-tight tracking-tight md:text-lg transition-colors duration-200 ${isActive ? "text-primary" : "text-primary/70"}`}
										>
											{entry.title}
										</h2>

										{/* Improved description with better spacing */}
										<p
											className={`text-xs leading-relaxed md:text-sm transition-all duration-300 ${
												isActive
													? "text-muted-foreground line-clamp-none"
													: "text-muted-foreground/80 line-clamp-2"
											}`}
										>
											{entry.description}
										</p>
									</div>

									{/* Enhanced expandable content */}
									<div
										aria-hidden={!isActive}
										className={`grid transition-all duration-500 ease-out ${
											isActive
												? "grid-rows-[1fr] opacity-100"
												: "grid-rows-[0fr] opacity-0"
										}`}
									>
										<div className="overflow-hidden">
											<div className="space-y-4 pt-2">
												{entry.items && entry.items.length > 0 && (
													<div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
														<ul className="space-y-2">
															{entry.items.map((item, itemIndex) => (
																<li
																	// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
																	key={itemIndex}
																	className="flex items-start gap-2 text-sm text-muted-foreground"
																>
																	<div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary/60 flex-shrink-0" />
																	<span className="leading-relaxed">
																		{item}
																	</span>
																</li>
															))}
														</ul>
													</div>
												)}

												{entry.button && (
													<div className="flex justify-end">
														<Button
															variant="default"
															size="sm"
															className="group hover:bg-primary hover:text-primary-foreground font-normal transition-all duration-200"
															asChild
														>
															<a
																href={entry.button.url}
																target="_blank"
																rel="noreferrer"
															>
																{entry.button.text}
																<ArrowUpRight className="ml-1.5 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
															</a>
														</Button>
													</div>
												)}
											</div>
										</div>
									</div>
								</div>
							</article>
						</div>
					);
				})}
			</div>
		</section>
	);
}
